// React-Query hooks для Sprint 5 A-S5-2 / A-S5-3 / A-S5-6.
// Source of truth: docs/specs/sprint-5/stage-4-frontend.md §2.3.2 + stage-1-contracts.md §6.5.

import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  BiomarkerDetailResponse,
  BiomarkerExtrasResponse,
  BiomarkerListFilter,
  BiomarkersListResponse,
  RequestAddExtraMarkersRequest,
  RequestAddExtraMarkersResponse,
  TimelinePeriod,
} from '@/api/types';

// RF-S5-S4-1: relative paths — baseURL уже = `${VITE_API_URL}/api/v1`.

export function useBiomarkersList(
  filter: BiomarkerListFilter = 'all',
): UseQueryResult<BiomarkersListResponse> {
  return useQuery({
    queryKey: queryKeys.biomarkersList(filter),
    queryFn: async () => {
      const { data } = await api.get<BiomarkersListResponse>('/biomarkers', {
        params: { filter },
      });
      return data;
    },
  });
}

export function useBiomarkerDetail(
  code: string,
  period: TimelinePeriod = 'all', // HEAL-8: дефолт — «Все» (консистентно с BiomarkerDetail)
): UseQueryResult<BiomarkerDetailResponse> {
  return useQuery({
    queryKey: queryKeys.biomarkerDetail(code, period),
    queryFn: async () => {
      const { data } = await api.get<BiomarkerDetailResponse>(
        `/biomarkers/${encodeURIComponent(code)}`,
        { params: { period } },
      );
      return data;
    },
    enabled: !!code,
  });
}

export function useBiomarkersExtras(): UseQueryResult<BiomarkerExtrasResponse> {
  return useQuery({
    queryKey: queryKeys.biomarkersExtras,
    queryFn: async () => {
      const { data } = await api.get<BiomarkerExtrasResponse>('/biomarkers/extras');
      return data;
    },
  });
}

export function useRequestAddExtraMarkers(): UseMutationResult<
  RequestAddExtraMarkersResponse,
  unknown,
  number[]
> {
  return useMutation({
    mutationFn: async (biomarkerIds: number[]) => {
      const body: RequestAddExtraMarkersRequest = { biomarker_ids: biomarkerIds };
      const { data } = await api.post<RequestAddExtraMarkersResponse>(
        '/biomarkers/extras/request-add',
        body,
      );
      return data;
    },
  });
}
